import { useCallback, useEffect, useState } from "react"

import { ArrowBackIcon } from "@chakra-ui/icons"
import { Box, Button, IconButton } from "@chakra-ui/react"
import { useNavigate, useParams } from "react-router-dom"

import { useAppSelector } from "app/hooks"
import FeeNameTable, { FeeMappingItem } from "components/table/fee-name-table"
import PageSelector from "components/table/page-selector"
import TemplateHeader from "components/template/template-header"
import useToastHook from "hooks/useToastHook/useToastHook"
import { apiClient } from "services/client"
import { CategoryData } from "services/common"
import { UpdateCategoryResponse } from "services/fee-mapping"
import { selectCurrentPmc } from "store/auth/authSlice"

import { ASC, defautCategory, SortOrder } from "../FeeMapping"
import FeeMappingPagination from "../FeeMappingPagination"

const FeeMappingDetailPage = () => {
  const { name } = useParams()
  const currentPmc = useAppSelector(selectCurrentPmc)
  const [feeData, setFeeData] = useState<CategoryData[]>([])
  const [pageSize, setPageSize] = useState<number>(5)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isUpdatedCategoryLoading, setIsUpdatedCategoryLoading] =
    useState<boolean>(false)
  const [sortConfig, setSortConfig] = useState<{
    key: keyof CategoryData | null
    order: SortOrder
  }>({ key: defautCategory, order: ASC })
  const [pageStartIndex, setPageStartIndex] = useState<number>(0)

  const [updatedCategoryData, setUpdatedCategoryData] = useState<
    FeeMappingItem[]
  >([])
  const displayToast = useToastHook()
  const navigate = useNavigate()

  const fetchFeeData = useCallback(async () => {
    setIsLoading(true)

    if (currentPmc?.pmc_id) {
      try {
        const response = await apiClient?.getFeeData({
          params: {
            pmcId: currentPmc?.pmc_id || "",
            limit: pageSize,
            pmsFeeName: name || "",
            skip: pageSize * pageStartIndex,
            sortBy: sortConfig.key || "",
            sortDirection: sortConfig?.order,
          },
        })
        setFeeData(response.data.data)
      } catch (err) {
        console.log(err)
      } finally {
        setIsLoading(false)
      }
    }
  }, [
    currentPmc?.pmc_id,
    pageStartIndex,
    sortConfig.key,
    name,
    pageSize,
    sortConfig?.order,
  ])

  useEffect(() => {
    const loadData = async () => {
      await fetchFeeData()
    }
    loadData().catch(console.error)
  }, [fetchFeeData])

  const updateCategoryHandler = async () => {
    if (currentPmc?.pmc_id) {
      setIsUpdatedCategoryLoading(true)
      try {
        await apiClient
          .updateFeeCategoryDataByName({
            params: { pmcId: currentPmc?.pmc_id, feeName: name || "" },
            data: updatedCategoryData,
          })
          .then(async (response: UpdateCategoryResponse) => {
            if (response?.data?.apiStatus.toLowerCase() !== "success") {
              displayToast({
                message: response?.data?.errorMessage || "",
                variant: "error",
              })
            }
            setIsUpdatedCategoryLoading(false)
            setUpdatedCategoryData([])
            await fetchFeeData()

            return true
          })
      } catch (error) {
        console.log(error)
        setIsUpdatedCategoryLoading(false)
      }
    }
  }

  const goBack = () => {
    navigate(-1)
  }

  return (
    <div>
      {" "}
      <Box>
        <Box display='flex'>
          <IconButton
            mr={5}
            size='sm'
            variant='outline'
            aria-label='Subtract'
            icon={<ArrowBackIcon h='30px' w='30px' />}
            data-testid='back'
            onClick={goBack}
          />
          <TemplateHeader text={name || ""} />
        </Box>
        <Box>
          <FeeNameTable
            categoryData={feeData}
            isLoading={isLoading}
            pageSize={pageSize}
            setUpdatedCategoryData={setUpdatedCategoryData}
            onSort={setSortConfig}
          />
          <Box display='flex' flexDirection='column' gap={4} mt={4}>
            <Box
              display='flex'
              justifyContent='flex-end'
              alignItems='center'
              gap={4}
            >
              <PageSelector
                pageSize={pageSize}
                setPageSize={setPageSize}
                setPageStartIndex={setPageStartIndex}
              />

              <FeeMappingPagination
                pageSize={pageSize}
                itemsLength={feeData.length}
                pageStartIndex={pageStartIndex}
                setPageStartIndex={setPageStartIndex}
              />
            </Box>
            <Button
              width='10%'
              height='40px'
              alignSelf='flex-end'
              onClick={updateCategoryHandler}
              disabled={
                updatedCategoryData.length === 0 || isUpdatedCategoryLoading
              }
              isLoading={isUpdatedCategoryLoading}
              mb='20px'
            >
              Save
            </Button>
          </Box>
        </Box>
      </Box>
    </div>
  )
}

export default FeeMappingDetailPage
